import { DILEMMAS_STATUS } from "../constants";
import type { ApiDilemma, ApiUploadedFile } from "../types";

export function isDilemmaPublishable(dilemma: ApiDilemma): boolean {
  const hasFilledSynthesis = dilemma.synthesis.trim().length > 0;
  const hasQuestions = dilemma.questions.length > 0;
  const allAnswersHaveComments = dilemma.questions.every((q) =>
    q.answers.every((a) => a.comment.trim().length > 0),
  );
  return hasFilledSynthesis && hasQuestions && allAnswersHaveComments;
}

export interface Step1Snapshot {
  title: string;
  description: string;
  topic: string;
  default_language: string;
}

export interface Step2AnswerSnapshot {
  payload: string;
  isCorrect: boolean;
}

export interface Step2Snapshot {
  question: string;
  answers: Step2AnswerSnapshot[];
}

export interface Step3Snapshot {
  synthesis: string;
  documentationLinks: string[];
  uploadedFiles: ApiUploadedFile[];
  reviewComment: string;
}

export function hasStep1Changes(
  initial: ApiDilemma | null,
  current: Step1Snapshot,
): boolean {
  if (!initial) return false;
  if ((current.title ?? "") !== (initial.name ?? "")) return true;
  if ((current.description ?? "") !== (initial.description ?? "")) return true;
  if ((current.topic ?? "") !== (initial.topic?.id ?? "")) return true;
  const initialLanguage = initial.default_language ?? initial.source_language;
  if ((current.default_language ?? "fr") !== initialLanguage) return true;
  return false;
}

export function hasStep2Changes(
  initial: ApiDilemma | null,
  current: Step2Snapshot,
): boolean {
  if (!initial) return false;
  const initialQuestion = initial.questions[0];
  if (!initialQuestion) return false;
  if ((current.question ?? "") !== (initialQuestion.payload ?? "")) {
    return true;
  }
  const maxLen = Math.max(
    current.answers.length,
    initialQuestion.answers.length,
  );
  for (let i = 0; i < maxLen; i++) {
    const cur = current.answers[i];
    const orig = initialQuestion.answers[i];
    if (!cur || !orig) return true;
    if (cur.payload !== orig.payload) return true;
    if (cur.isCorrect !== orig.is_correct) return true;
  }
  return false;
}

export function hasStep3Changes(
  initial: ApiDilemma | null,
  current: Step3Snapshot,
): boolean {
  if (!initial) return false;

  if ((current.synthesis ?? "") !== (initial.synthesis ?? "")) {
    return true;
  }

  const initialLinks = initial.documentation_links.map((l) =>
    l.replace(/^https?:\/\//i, ""),
  );
  const currentLinks = current.documentationLinks.filter(
    (l) => l.trim() !== "",
  );
  if (currentLinks.length !== initialLinks.length) return true;
  if (currentLinks.some((link, i) => link !== initialLinks[i])) return true;

  const initialFileIds = initial.uploaded_files
    .map((f) => f.id)
    .slice()
    .sort();
  const currentFileIds = current.uploadedFiles
    .map((f) => f.id)
    .slice()
    .sort();
  if (initialFileIds.length !== currentFileIds.length) return true;
  if (initialFileIds.some((id, i) => id !== currentFileIds[i])) return true;

  if ((current.reviewComment ?? "") !== (initial.review_comment ?? "")) {
    return true;
  }

  return false;
}

export function statusBadgeVariant(
  status: string,
):
  | "dilemma_published"
  | "dilemma_archived"
  | "dilemma_scheduled"
  | "dilemma_submitted"
  | "secondary" {
  switch (status) {
    case DILEMMAS_STATUS.PUBLISHED:
      return "dilemma_published";
    case DILEMMAS_STATUS.ARCHIVED:
      return "dilemma_archived";
    case DILEMMAS_STATUS.SCHEDULED:
      return "dilemma_scheduled";
    case DILEMMAS_STATUS.SUBMITTED:
      return "dilemma_submitted";
    default:
      return "secondary";
  }
}
